import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ArrowLeft, DollarSign, Upload, FileText, Download, Copy } from "lucide-react";
import { motion } from "framer-motion";

export default function TovSolucoes() {
  const [csvData, setCsvData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  // Função para remover acentos e converter para maiúsculo
  const removerAcentosEMaiusculo = (texto) => {
    // Mapa de substituição de caracteres acentuados
    const mapaAcentos = {
      'á': 'a', 'à': 'a', 'ã': 'a', 'â': 'a', 'ä': 'a',
      'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
      'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i',
      'ó': 'o', 'ò': 'o', 'õ': 'o', 'ô': 'o', 'ö': 'o',
      'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u',
      'ç': 'c',
      'ñ': 'n',
      'Á': 'A', 'À': 'A', 'Ã': 'A', 'Â': 'A', 'Ä': 'A',
      'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E',
      'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I',
      'Ó': 'O', 'Ò': 'O', 'Õ': 'O', 'Ô': 'O', 'Ö': 'O',
      'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U',
      'Ç': 'C',
      'Ñ': 'N'
    };
    
    // Substituir caracteres acentuados usando o mapa
    let resultado = texto;
    Object.keys(mapaAcentos).forEach(char => {
      resultado = resultado.replace(new RegExp(char, 'g'), mapaAcentos[char]);
    });
    
    // Tentar também com normalize como fallback
    resultado = resultado
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    
    // LIMPEZA AGRESSIVA: Remover qualquer caractere que não seja ASCII imprimível
    // Mantém apenas letras, números, espaços e caracteres básicos de pontuação
    resultado = resultado.replace(/[^\x20-\x7E]/g, '');
    
    return resultado.toUpperCase();
  };

  // Função para remover vírgulas da descrição (CRÍTICO)
  const removerVirgulas = (texto) => {
    return texto.replace(/,/g, '');
  };

  // Função para processar o valor monetário do formato "R$ 12.897,76"
  const parseValue = (value) => {
    if (!value || value === '---') return 0;
    // Remove "R$", espaços, e pontos (separadores de milhar), converte vírgula para ponto
    const cleanValue = value
      .replace(/R\$/g, '')
      .replace(/\s/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Função para formatar data no formato DDMMAAAA
  const formatarDataCSV = (dataOriginal) => {
    // Exemplo: "02/01/2025" -> "02012025"
    const match = dataOriginal.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (match) {
      return `${match[1]}${match[2]}${match[3]}`;
    }
    return '01012025'; // Fallback
  };

  // Função para extrair competência do CSV (MMAAAA)
  const extrairCompetencia = () => {
    if (!csvData.length) {
      return '012025'; // Fallback
    }
    
    // Pegar a primeira transação para extrair mês/ano
    const primeiraData = csvData[0].data;
    const match = primeiraData.match(/\d{2}\/(\d{2})\/(\d{4})/);
    
    if (match) {
      const mes = match[1];
      const ano = match[2];
      return `${mes}${ano}`;
    }
    
    return '012025'; // Fallback
  };

  // Dicionário de mapeamento: Nome -> Código de Débito
  // Mapeamento extraído do CSV final para nomes com Lancamento = 'D'
  const MAPEAMENTO_NOMES_CODIGO = {
    'ADAILTON DE JESUS SILVA': 4867,
    'ADAILTON DE JESUS SIL': 4867, // Variação truncada
    'ALEXANDRE DA SILVA BARBOSA': 4867,
    'ALEXANDRE DA SILVA BA': 4867, // Variação truncada
    'ALEXANDRE GHION': 4867,
    'AMADEU BATISTA DOS SANTOS': 4867,
    'AMADEU BATISTA DOS SA': 4867, // Variação truncada
    'ANA CLAUDIA DOS SANTOS DA CRUZ': 4867,
    'ANA CLAUDIA DOS SANTO': 4867, // Variação truncada
    'ANDERSON LOPES DE JESUS': 4867,
    'ANDERSON LOPES DE JES': 4867, // Variação truncada
    'ANDRE HELENO DA SILVA': 4867,
    'ANTHONY VALERIANO PAIVA': 4328,
    'ANTHONY VALERIANO PAI': 4328, // Variação truncada
    'CAMILA MARTINS PEREIRA': 4867,
    'CAMILLA STEPHANIE GNAN GONCALVES SAMPAIO': 4867,
    'CAMILLA STEPHANIE': 4867, // Variação truncada
    'CHRISPIM MENDES FILHO': 4867,
    'CLAUDIO DE OLIVEIRA MARTINS': 4867,
    'CLAUDIO OLIVEIRA MART': 4867, // Variação truncada
    'CLAUDIO JOSE MARIANO': 4867,
    'CLAUDIO JOSÉ MARIANO': 4867, // Variação com acento
    'CLAUDIO JOS MARIANO': 4867, // Variação com letras faltando (� apaga letra anterior)
    'CLAYTON SANTOS NOCETTI': 4867,
    'CLAYTON SANTOS NOCETT': 4867, // Variação truncada
    'DAVID ROSARIO': 4867,
    'DEIVID LUIS NOGUEIRA': 4867,
    'DEIVID LUIZ NOGUEIRA': 4867, // Variação com Z
    'EDUARDO PEDROZO CARDOSO': 4867,
    'EDNAMAURA DOS SANTOS': 4867,
    'FABIANO DE JESUS SANTOS': 4867,
    'FERNANDA REGINA DOS SANTOS SILVA': 4867,
    'GABRIELLY GARCIA DO NASCIMENTO': 4867,
    'GABRIELLY GARCIA DO N': 4867, // Variação truncada
    'GERALDA JOANA PASTOR': 4867,
    'GERMANO LOPES DOS SANTOS JUNIOR': 4867,
    'GILDEMAR BAZAN FRANCISCO': 4867,
    'GILSON MOREIRA DA SILVA': 4867,
    'GILSON MOREIRA DA SIL': 4867, // Variação truncada
    'GIOVANI SANTOS DA SILVA': 4867,
    'GIOVANI SANTOS DA SIL': 4867, // Variação truncada
    'GOTO COMUNICACAO UNIFICADA DO BRASIL LTDA': 4186,
    'IASMIM MELQUIEDES DOS SANTOS': 4867,
    'IGOR DA CRUZ': 4867,
    'IGOR RAPHAEL ALVES': 4867,
    'IGOR SOUSA SANTOS': 4867,
    'INGRID ALVES DE SA': 4867,
    'INGRID ALVES': 4867, // Variação truncada
    'IVAN EVANGELISTA DA SILVA': 4867,
    'IVAN EVANGELISTA DA S': 4867, // Variação truncada
    'JEAN ANTONYOS HALBERSTADT DOS SANTOS': 4867,
    'JEAN ANTONYOS HALBERS': 4867, // Variação truncada
    'JEFFERSON DA SILVA GARAJAU': 4867,
    'JENIFFER DOS SANTOS GOMES': 4867,
    'JENIFFER DOS SANTOS G': 4867, // Variação truncada
    'JOAQUIM SIMPLICIO FERREIRA NETTO': 4867,
    'JOAQUIM SIMPLICIO FER': 4867, // Variação truncada
    'JONH REIS ARAUJO': 4867,
    'JOHN REIS ARAUJO': 4867, // Variação sem H
    'JORGE HENRIQUE GOIS NAPOLEAO': 4867,
    'JOSE ANTONIO DOS SANTOS': 4867,
    'JOSÉ ANTONIO DOS SANTOS': 4867, // Variação com acento
    'JOS� ANT�NIO DOS SANT': 4867, // Variação com encoding quebrado e truncada
    'JOSE ANTONIO DOS SANT': 4867, // Variação normalizada truncada
    'JOS ANTNIO DOS SANT': 4867, // Variação com letras faltando (� apaga letra anterior)
    'JOSE DO CARMO ALVES': 4867,
    'JOSÉ DO CARMO': 4867, // Variação com acento e truncada
    'JOS DO CARMO': 4867, // Variação com letras faltando (� apaga letra anterior)
    'JOSE MANOEL DA SILVA': 4867,
    'JOSÉ MANOEL DA SILVA': 4867, // Variação com acento
    'JOS MANOEL DA SILVA': 4867, // Variação com letras faltando (� apaga letra anterior)
    'KARINA FERNANDES DIOGO': 4867,
    'KARINA FERNANDES DIOG': 4867, // Variação truncada
    'KEVIN ALVES NUNES': 4867,
    'LEANDRO ALMEIDA LEONCIO': 4867,
    'LEANDRO ALMEIDA LEONC': 4867, // Variação truncada
    'LILIAN MARTINS C NASCIMENTO': 4867,
    'LILIAN MARTINS DA CONCEICAO NASCIMENTO': 4867,
    'LUCINEIA APARECIDA DOS SANTOS OLIVEIRA': 4867,
    'LUCINEIA APARECIDA DO': 4867, // Variação truncada
    'LUIZ CARLOS DO CARMO': 4867,
    'MARCO ANTONIO FERRO GALVAO DE CAMARGO': 4867,
    'MARCOS ANTONIO LOMBARDI JUNIOR': 4867,
    'MARCOS ANT�NIO LOMBAR': 4867, // Variação com encoding quebrado e truncada
    'MARCOS ANTONIO LOMBAR': 4867, // Variação normalizada truncada
    'MARCOS ANTNIO LOMBAR': 4867, // Variação com letras faltando (� apaga letra anterior)
    'MARCOS CEZAR MARTINS DOS SANTO': 4867,
    'MARCOS JUNIOR': 4867,
    'MARCOS ROBERTO PALMEIRA': 4867,
    'MARCOS VINICIUS DOS SANTOS': 4867,
    'MARCOS VINICIUS DOS S': 4867, // Variação truncada
    'MARIA EDUARDA DE OLIVEIRA SOUZA': 4867,
    'MARIA REJANE PEREIRA ANDRE': 4867,
    'MARINALDA DE SOUSA BORGES SILVA': 4867,
    'MATHEUS COSTA': 4867,
    'MATHEUS COSTA E SILVA': 4867,
    'MAXIMO DIGITAL': 4866,
    'NATALIA DOS SANTOS SOUSA': 4867,
    'NATALIA DOS SANTOS SO': 4867, // Variação truncada
    'NELSON DA SILVA': 4867,
    'NOE NEGRAO DA SILVA': 4867,
    'ODAIR DAS DORES': 4867,
    'OSEAS AURELIANO DA SILVA': 4867,
    'OSEAS AURELIANO DA SI': 4867, // Variação truncada
    'OSMIR SANTOS SILVA': 4867,
    'OTAVIO SALLES ZANELATO': 4867,
    'OT�VIO SALLES ZANELAT': 4867, // Variação com encoding quebrado e truncada
    'OTAVIO SALLES ZANELAT': 4867, // Variação normalizada truncada
    'OTVIO SALLES ZANELAT': 4867, // Variação com letras faltando (� apaga letra anterior)
    'PAROQUIA N SRA PERPETUO SOCORRO E STA ROSALIA': 4248,
    'RENE ANTONIO FRANCELINO': 4867,
    'REN� ANT�NIO FRANCELI': 4867, // Variação com encoding quebrado e truncada
    'RENE ANTONIO FRANCELI': 4867, // Variação normalizada truncada
    'REN ANTNIO FRANCELI': 4867, // Variação com letras faltando (� apaga letra anterior)
    'RINALDO RODRIGUES DA SILVA': 4867,
    'RINALDO RODRIGUES DA': 4867, // Variação truncada
    'ROBSON ROBERTO FAGUNDES': 4032,
    'RODRIGO SOARES AVELINO DA SILVA': 4867,
    'ROGERIO ALMEIDA ZANELATO': 4867,
    'ROGERIO DE ALMEIDA ZANELATO': 4867,
    'ROG�RIO DE ALMEIDA ZA': 4867, // Variação com encoding quebrado e truncada
    'ROGERIO DE ALMEIDA ZA': 4867, // Variação normalizada truncada
    'ROGRIO DE ALMEIDA ZA': 4867, // Variação com letras faltando (� apaga letra anterior)
    'SA ASSESSORIA EMPRESARIAL LTDA': 4247,
    'TAMIRES LIMA REIS': 4867,
    'ADRIANA DE MORAES FERREIRA': 4867,
    'ADRIANA DE MORAES FER': 4867, // Variação truncada
    'RAYANNE DE LIMA GOMES': 4867,
    'THAINA SOLEDADE DOS SANTOS': 4867,
    'THAINA SOLEDADE DOS SANTOS MALAVAZI': 4867,
    'UFOS TATOO COMERCIO E SERVICOS ESPECIALIZADOS LTDA': 4866,
    'WINDSON MESSIAS ROCHA': 4867
  };

  // Função auxiliar para remover acentos e normalizar texto
  const removerAcentosENormalizar = (texto) => {
    if (!texto) return '';
    
    const mapaAcentos = {
      'á': 'a', 'à': 'a', 'ã': 'a', 'â': 'a', 'ä': 'a',
      'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
      'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i',
      'ó': 'o', 'ò': 'o', 'õ': 'o', 'ô': 'o', 'ö': 'o',
      'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u',
      'ç': 'c',
      'ñ': 'n',
      'Á': 'A', 'À': 'A', 'Ã': 'A', 'Â': 'A', 'Ä': 'A',
      'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E',
      'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I',
      'Ó': 'O', 'Ò': 'O', 'Õ': 'O', 'Ô': 'O', 'Ö': 'O',
      'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U',
      'Ç': 'C',
      'Ñ': 'N'
    };
    
    let resultado = texto;
    for (const [comAcento, semAcento] of Object.entries(mapaAcentos)) {
      resultado = resultado.replace(new RegExp(comAcento, 'g'), semAcento);
    }
    
    return resultado
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\x20-\x7E]/g, '')
      .toUpperCase()
      .trim();
  };

  // Função para detectar nome na descrição e retornar código de débito
  const detectarCodigoDebitoPorNome = (descricao) => {
    if (!descricao) return null;
    
    const descricaoNormalizada = removerAcentosENormalizar(descricao);
    
    // Verificar cada nome do dicionário
    for (const [nome, codigo] of Object.entries(MAPEAMENTO_NOMES_CODIGO)) {
      // Match exato primeiro
      if (descricaoNormalizada.includes(nome)) {
        return codigo;
      }
    }
    
    // Se não encontrou match exato, tentar match parcial (primeiras 3-4 palavras do nome)
    // Isso resolve o problema de nomes truncados no CSV de origem
    for (const [nome, codigo] of Object.entries(MAPEAMENTO_NOMES_CODIGO)) {
      const palavrasNome = nome.split(' ');
      
      // Para nomes com 3+ palavras, verificar se as primeiras palavras estão presentes
      if (palavrasNome.length >= 3) {
        // Pegar as primeiras 3 palavras (ou 2 se o nome tiver só 2 palavras principais)
        const palavrasChave = palavrasNome.slice(0, Math.min(3, palavrasNome.length)).join(' ');
        if (palavrasChave.length >= 10 && descricaoNormalizada.includes(palavrasChave)) {
          return codigo;
        }
      }
      
      // Para nomes com 2 palavras, verificar se as 2 palavras estão presentes
      if (palavrasNome.length === 2) {
        const palavrasChave = palavrasNome.join(' ');
        if (palavrasChave.length >= 8 && descricaoNormalizada.includes(palavrasChave)) {
          return codigo;
        }
      }
    }
    
    return null;
  };

  // Função para processar o CSV
  const processCSV = (csvText) => {
    let lines = csvText.split('\n').filter(line => line.trim());
    
    // ======== DETECÇÃO E CORREÇÃO DE COLUNA EXTRA ========
    // Verificar se há coluna extra no CSV (6 colunas ao invés de 5)
    if (lines.length > 0) {
      const cabecalho = lines[0];
      const colunasCabecalho = cabecalho.split(';');
      
      // Detectar padrões de coluna extra:
      // 1. Cabeçalho com 6+ colunas (padrão deveria ter 5: DATA, DESCRIÇÃO, C/D, VALOR, SALDO)
      // 2. Padrão específico DESCRIÇÃO;;C/D (coluna vazia entre descrição e C/D)
      // 3. Padrão com ;; seguido de aspas ou '+
      // 4. Padrão com coluna OBSERVAÇÃO no final
      const temColunaExtra = colunasCabecalho.length > 5 || 
                             cabecalho.includes(';;C/D') || 
                             cabecalho.includes(";;'");
      
      const tem6Colunas = colunasCabecalho.length === 6;
      
      if (temColunaExtra || tem6Colunas) {
        console.log('⚠️ DETECTADA COLUNA EXTRA NO CSV!');
        console.log(`📋 Estrutura original: ${cabecalho}`);
        console.log(`📋 Colunas detectadas: ${colunasCabecalho.length} (esperado: 5)`);
        console.log('🔧 Corrigindo estrutura do CSV...');
        
        let linhasCorrigidas = 0;
        let linhasComDados = 0;
        
        // Processar todas as linhas para remover/mesclar a coluna extra
        lines = lines.map((linha, index) => {
          const colunas = linha.split(';');
          
          // CASO 1: Se tiver exatamente 6 colunas (formato agosto com OBSERVAÇÃO)
          // Formato: DATA;DESCRIÇÃO;C/D;VALOR;SALDO;OBSERVAÇÃO
          if (colunas.length === 6) {
            // Remover a última coluna (OBSERVAÇÃO)
            colunas.pop();
            
            // Fazer trim em todas as colunas para remover espaços
            for (let i = 0; i < colunas.length; i++) {
              colunas[i] = colunas[i].trim();
            }
            
            linhasCorrigidas++;
            return colunas.join(';');
          }
          
          // CASO 2: Se tiver mais de 6 colunas (estrutura com coluna vazia no meio)
          if (colunas.length > 6) {
            const colunaExtra = colunas[2] || ''; // Coluna no índice 2 (terceira coluna)
            
            // Se a coluna extra tiver dados, mesclar com a descrição principal
            if (colunaExtra.trim() !== '') {
              colunas[1] = `${colunas[1]} - ${colunaExtra.trim()}`; // Mesclar com a descrição
              linhasComDados++;
              if (linhasComDados <= 3) {
                console.log(`  📝 Mesclando coluna extra: "${colunaExtra}" -> Descrição final: "${colunas[1].substring(0, 60)}..."`);
              }
            }
            
            // Remover a coluna extra (índice 2)
            colunas.splice(2, 1);
            linhasCorrigidas++;
            
            return colunas.join(';');
          }
          
          return linha;
        });
        
        console.log(`✅ Estrutura normalizada! ${linhasCorrigidas} linhas corrigidas`);
        console.log(`📋 Linhas com dados na coluna extra (mescladas): ${linhasComDados}`);
        console.log(`📋 Nova estrutura: ${lines[0]}`);
        console.log(`📋 Exemplo linha 2: ${lines[1]}`);
      } else {
        console.log('✅ Formato CSV padrão detectado (5 colunas)');
        console.log(`📋 Estrutura: ${cabecalho}`);
      }
    }
    // ======== FIM DA CORREÇÃO ========
    
    const data = [];
    
    let totalRecebimentos = 0;
    let valorTotalRecebimentos = 0;
    let totalPagamentos = 0;
    let valorTotalPagamentos = 0;
    let totalTarifasTaxas = 0;
    let valorTotalTarifasTaxas = 0;
    let totalTarifasRJX = 0;
    let valorTarifasRJX = 0; // Tarifas RJX vão para CSV mas não afetam cálculo saldo
    let valorTotalRJX = 0;
    let saldoInicial = 0;
    let totalTransacoes = 0;
    let ultimoTituloRJX = ''; // Armazenar o número do último título RJX processado
    let totalCancelamentos = 0;

    // Primeira passagem: identificar IDs de transações canceladas
    const idsCancelados = new Set();
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(';');
      if (values.length >= 2) {
        const descricao = values[1] || '';
        if (descricao.toLowerCase().includes('cancelamento')) {
          // Extrair ID da transação (ex: "Cancelamento Pagamento 4010" -> "4010")
          const match = descricao.match(/\b(\d{4})\b/);
          if (match) {
            idsCancelados.add(match[1]);
          }
        }
      }
    }
    
    if (idsCancelados.size > 0) {
      console.log('🔍 IDs de transações canceladas detectados:', Array.from(idsCancelados).join(', '));
    }

    // Pular a primeira linha (cabeçalho) e capturar o saldo inicial da segunda linha
    for (let i = 1; i < lines.length; i++) {
      // Capturar o saldo inicial (primeira linha após cabeçalho - Saldo Anterior)
      if (i === 1) {
        const values = lines[i].split(';');
        if (values.length >= 4) {
          saldoInicial = parseValue(values[3]); // Valor da coluna "VALOR"
        }
        continue; // Pular a linha do Saldo Anterior no processamento
      }
      const values = lines[i].split(';');
      
      if (values.length >= 4) {
        const descricao = values[1] || '';
        const tipoOperacao = values[2] || '';
        const valor = parseValue(values[3]);
        const saldo = parseValue(values[4]);
        
        // Verificar se é transação de cancelamento (devem ser ignoradas)
        const isCancelamento = descricao.toLowerCase().includes('cancelamento');
        
        // Pular transações de cancelamento
        if (isCancelamento) {
          totalCancelamentos++;
          console.log(`⚠️ CANCELAMENTO DETECTADO (ignorado): ${descricao.substring(0, 80)} | Valor: ${valor.toFixed(2)}`);
          continue;
        }
        
        // Verificar se esta transação possui um ID que foi cancelado
        let transacaoCancelada = false;
        for (const idCancelado of idsCancelados) {
          if (descricao.includes(idCancelado)) {
            transacaoCancelada = true;
            totalCancelamentos++;
            console.log(`⚠️ TRANSAÇÃO CANCELADA (ignorada): ID ${idCancelado} | ${descricao.substring(0, 80)} | Valor: ${valor.toFixed(2)}`);
            break;
          }
        }
        
        if (transacaoCancelada) {
          continue;
        }
        
        // Detectar créditos de operação convencional (com diferentes encodings possíveis)
        const descLower = descricao.toLowerCase();
        const isCreditoOperacao = descLower.includes('crédito') && descLower.includes('operação') ||
                                  descLower.includes('credito') && descLower.includes('operacao') ||
                                  descLower.includes('cr�dito') && descLower.includes('opera��o'); // encoding quebrado
        const isRecebimento = descLower.includes('recebimento') || isCreditoOperacao;
        const isRJXTerceirizacoes = descricao.toUpperCase().includes('RJX TERCEIRIZACOES');
        const isEJXConsultoria = descricao.toUpperCase().includes('EJX CONSULTORIA');
        
        // Determinar se é débito ou crédito
        const isDebito = tipoOperacao.includes('-');
        const isCredito = tipoOperacao.includes('+');
        
        // Log para créditos de operação convencional
        if (isCreditoOperacao) {
          console.log(`💰 CRÉDITO DE OPERAÇÃO CONVENCIONAL DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        
        // Debug: log transações com "+" que não são recebimento
        if (tipoOperacao.includes('+') && !isRecebimento && i < 60) {
          console.log(`🔍 DEBUG: Transação com "+" não detectada como recebimento:`, descricao.substring(0, 80));
        }
        
        // Verificar se é tarifa de pagamento (pode estar vinculada a RJX)
        const isTarifaPagamento = descricao.toLowerCase().includes('tarifa administrativa pagamento');
        const isTarifa = descricao.toLowerCase().includes('tarifa');
        const isReembolso = descricao.toLowerCase().includes('reembolso');
        const isTaxa = descricao.toLowerCase().includes('taxa');
        const isJuros = descricao.toLowerCase().includes('juros');
        
        // Pagamento só é classificado como tal se NÃO for uma tarifa/reembolso/taxa
        const isPagamento = descricao.toLowerCase().includes('pagamento') && !isTarifa && !isReembolso && !isTaxa;
        
        // Detectar códigos especiais para débitos customizados
        const isCodigoDebito4873 = descricao.includes('0211.00012.0051431854.24-88');
        const isCodigoDebito4874 = descricao.includes('5.225.163');
        const isCodigoDebito4674Especifico = descricao.includes('5.307.342');
        const isCodigoDebito4510 = descricao.toLowerCase().includes('suhai seguradora');
        const isCodigoDebito4538 = descricao.toUpperCase().includes('SA ASSESSORIA EMPRESA');
        const isCodigoDebito4875 = descricao.toUpperCase().includes('ROGERIO DE JESUS');
        const isCodigoDebito4868 = descricao.toUpperCase().includes('TICKET SERVI');
        const isCodigoDebito4870 = descricao.toUpperCase().includes('SIND DOS EMPREG');
        const isCodigoDebito4864 = descricao.toUpperCase().includes('GO TO COMUNICA') || descricao.toUpperCase().includes('AO3 TECNOLOGIA') || descricao.toUpperCase().includes('INELTEC TECNOLOGIA');
        const isCodigoDebito4478 = descricao.toUpperCase().includes('CLARO');
        const isCodigoDebito4698 = descricao.toUpperCase().includes('PAGAMENTO') && descricao.toUpperCase().includes('PEND');
        const isCodigoDebito617 = descricao.toUpperCase().includes('EJX CONSULTORIA');
        
        // Detectar se é DARF (para aplicar 4674 como padrão se não houver regra específica)
        const isDARF = descricao.toUpperCase().includes('DARF');
        const isCodigoDebito4674 = isCodigoDebito4674Especifico || (isDARF && !isCodigoDebito4873 && !isCodigoDebito4874);
        
        // Log de códigos especiais detectados
        if (isCodigoDebito4873 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 4873 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        if (isCodigoDebito4874 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 4874 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        if (isCodigoDebito4674 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 4674 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        if (isCodigoDebito4510 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 4510 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        if (isCodigoDebito4538 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 4538 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        if (isCodigoDebito4875 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 4875 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        if (isCodigoDebito4868 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 4868 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        if (isCodigoDebito4870 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 4870 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        if (isCodigoDebito4864 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 4864 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        if (isCodigoDebito4478 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 4478 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        if (isCodigoDebito4698 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 4698 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        if (isCodigoDebito617 && isPagamento) {
          console.log(`💳 CÓDIGO ESPECIAL 617 DETECTADO: ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        
        // Se for um PAGAMENTO RJX (débito), extrair o número do título (RJX será excluído do CSV)
        if (isRJXTerceirizacoes && isDebito) {
          const match = descricao.match(/(TRA|DMR)\s+\d+(\s*-\s*\d+)?/i);
          if (match) {
            ultimoTituloRJX = match[0].replace(/\s+/g, ' ').trim(); // Normalizar espaços
            console.log(`🔴 PAGAMENTO RJX DETECTADO (EXCLUÍDO DO CSV): Título = "${ultimoTituloRJX}" | Valor: ${valor}`);
          }
        }
        
        // Se for um RECEBIMENTO de RJX (crédito), incluir no cálculo do saldo mas EXCLUIR do CSV final
        if (isRJXTerceirizacoes && isCredito) {
          console.log(`💚 RECEBIMENTO RJX DETECTADO (INCLUÍDO no cálculo do saldo, EXCLUÍDO do CSV final): ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        
        // Se for EJX, apenas log (EJX vai para o CSV com código 617)
        if (isEJXConsultoria) {
          console.log(`💼 PAGAMENTO EJX DETECTADO (ENTRA NO CSV com código 617): ${descricao.substring(0, 60)} | Valor: ${valor}`);
        }
        
        // Verificar se é uma tarifa vinculada ao último pagamento RJX
        const isTarifaRJX = isTarifaPagamento && ultimoTituloRJX && descricao.includes(ultimoTituloRJX);
        
        const row = {
          data: values[0],
          descricao: descricao,
          tipoOperacao: tipoOperacao,
          valor: valor,
          saldo: saldo,
          isRecebimento: isRecebimento && !(isRJXTerceirizacoes && isCredito), // Excluir RECEBIMENTOS RJX do CSV
          isPagamento: isPagamento && !(isRJXTerceirizacoes && isDebito), // Excluir PAGAMENTOS RJX do CSV
          isTarifa: isTarifa,
          isReembolso: isReembolso,
          isTaxa: isTaxa,
          isJuros: isJuros,
          isRJXPagamentoExcluido: isRJXTerceirizacoes && isDebito, // PAGAMENTO RJX excluído do CSV
          isRJXRecebimentoExcluido: isRJXTerceirizacoes && isCredito, // RECEBIMENTO RJX excluído do CSV
          isTarifaRJX: isTarifaRJX,
          codigoDebito4873: isCodigoDebito4873,
          codigoDebito4874: isCodigoDebito4874,
          codigoDebito4674: isCodigoDebito4674,
          codigoDebito4510: isCodigoDebito4510,
          codigoDebito4538: isCodigoDebito4538,
          codigoDebito4875: isCodigoDebito4875,
          codigoDebito4868: isCodigoDebito4868,
          codigoDebito4870: isCodigoDebito4870,
          codigoDebito4864: isCodigoDebito4864,
          codigoDebito4478: isCodigoDebito4478,
          codigoDebito4698: isCodigoDebito4698,
          codigoDebito617: isCodigoDebito617
        };
        
        data.push(row);
        totalTransacoes++;
        
        // Contar recebimentos (APENAS os que vão para o CSV - SEM RJX)
        if (row.isRecebimento) {
          totalRecebimentos++;
          valorTotalRecebimentos += valor;
        }
        
        // Contar pagamentos (APENAS os que vão para o CSV - SEM RJX)
        if (row.isPagamento) {
          totalPagamentos++;
          valorTotalPagamentos += valor;
        }
        
        // Contar TODAS as Tarifas, Taxas, Reembolsos e Juros com sinal '-' (débito)
        // INCLUINDO tarifas RJX - todas com sinal '-' afetam o saldo
        if ((row.isTarifa || row.isReembolso || row.isTaxa || row.isJuros) && isDebito) {
          totalTarifasTaxas++;
          valorTotalTarifasTaxas += valor;
          if (isTarifaRJX) {
            console.log(`🔍 TARIFA RJX DETECTADA (afeta saldo): ${descricao.substring(0, 60)} | Valor: ${valor} | Título: ${ultimoTituloRJX}`);
          } else if (isTarifaPagamento) {
            console.log(`🔍 TARIFA PAGAMENTO DETECTADA (afeta saldo): ${descricao.substring(0, 60)} | Valor: ${valor}`);
          }
        }
        
        // Contar valores RJX excluídos do CSV (pagamentos e recebimentos)
        // IMPORTANTE: Pagamentos RJX = débito (reduz saldo), Recebimentos RJX = crédito (aumenta saldo)
        if (row.isRJXPagamentoExcluido) {
          valorTotalRJX += valor; // Pagamento RJX (débito)
        }
        if (row.isRJXRecebimentoExcluido) {
          valorTotalRJX -= valor; // Recebimento RJX (crédito) - subtrai porque aumenta o saldo
        }
        
        // Log das primeiras 5 transações para debug
        if (i <= 5) {
          console.log(`📝 Transação ${i} (CSV):`, {
            data: row.data,
            descricao: row.descricao,
            valor: row.valor,
            isRecebimento: row.isRecebimento,
            isPagamento: row.isPagamento,
            isRJXPagamentoExcluido: row.isRJXPagamentoExcluido,
            isRJXRecebimentoExcluido: row.isRJXRecebimentoExcluido
          });
        }
      }
    }

    // O saldo final pode estar na última linha na coluna SALDO ou VALOR
    let saldoFinalCSV = 0;
    if (data.length > 0) {
      const ultimaLinha = data[data.length - 1];
      // Se o saldo da última linha for 0, usar o valor (caso da linha "Saldo")
      saldoFinalCSV = ultimaLinha.saldo !== 0 ? ultimaLinha.saldo : ultimaLinha.valor;
    }
    
    // Cálculo do Saldo Líquido (para o CSV gerado - SEM transações RJX)
    const saldoLiquidoCSV = saldoInicial + valorTotalRecebimentos - valorTotalPagamentos - valorTotalTarifasTaxas;

    console.log('='.repeat(80));
    console.log('📊 RESUMO DO PROCESSAMENTO DO CSV');
    console.log('='.repeat(80));
    console.log('💰 Saldo Inicial (linha 2):', saldoInicial.toFixed(2));
    console.log('📈 Total Recebimentos (SEM RJX):', totalRecebimentos, '| Valor:', valorTotalRecebimentos.toFixed(2));
    console.log('📉 Total Pagamentos (SEM RJX):', totalPagamentos, '| Valor:', valorTotalPagamentos.toFixed(2));
    console.log('⚡ Total Tarifas/Taxas/Reembolsos (TODAS com "-"):', totalTarifasTaxas, '| Valor:', valorTotalTarifasTaxas.toFixed(2));
    console.log('🚫 Transações RJX excluídas do CSV (Pagamentos - Recebimentos):', '| Valor líquido:', valorTotalRJX.toFixed(2));
    console.log('⚠️ Total Transações Canceladas (ignoradas):', totalCancelamentos);
    console.log('-'.repeat(80));
    console.log('🧮 CÁLCULO DO SALDO LÍQUIDO (para CSV - SEM RJX):');
    console.log('   Saldo Inicial:            +', saldoInicial.toFixed(2));
    console.log('   Recebimentos (SEM RJX):   +', valorTotalRecebimentos.toFixed(2));
    console.log('   Pagamentos (SEM RJX):     -', valorTotalPagamentos.toFixed(2));
    console.log('   Tarifas/Taxas (todas):    -', valorTotalTarifasTaxas.toFixed(2));
    console.log('   = Saldo Líquido CSV:       ', saldoLiquidoCSV.toFixed(2));
    console.log('-'.repeat(80));
    console.log('✅ Saldo Líquido CALCULADO (CSV): ', saldoLiquidoCSV.toFixed(2));
    console.log('📄 Saldo Final da Conta (Banco):  ', saldoFinalCSV.toFixed(2));
    console.log('❓ Diferença (deve ser ~0):       ', (saldoLiquidoCSV - saldoFinalCSV).toFixed(2));
    console.log('='.repeat(80));

    return {
      data,
      summary: {
        totalTransacoes,
        totalRecebimentos,
        valorTotalRecebimentos,
        totalPagamentos,
        valorTotalPagamentos,
        totalTarifasTaxas,
        valorTotalTarifasTaxas,
        totalTarifasRJX,
        valorTarifasRJX,
        valorTotalRJX,
        saldoInicial,
        saldoFinal: saldoFinalCSV
      }
    };
  };

  // Função para lidar com o upload do arquivo
  const handleFileUpload = (file) => {
    if (!file) return;
    
    setIsProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const result = processCSV(csvText);
        setCsvData(result.data);
        setSummary(result.summary);
      } catch (error) {
        console.error('Erro ao processar CSV:', error);
        alert('Erro ao processar o arquivo CSV. Verifique o formato do arquivo.');
      } finally {
        setIsProcessing(false);
      }
    };
    
    // Ler arquivo - o browser tentará detectar automaticamente o encoding
    reader.readAsText(file);
  };

  // Eventos de drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Função para formatar valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para gerar CSV formatado para Questor
  const gerarCSVFormatado = () => {
    if (!csvData.length) {
      alert('Por favor, faça upload do arquivo CSV primeiro.');
      return;
    }

    const linhasCSV = [];
    
    // Filtrar transações de recebimento, pagamento (exceto RJX) e outras (Tarifa, Reembolso, Taxa, Juros + Tarifas RJX)
    const transacoesRecebimento = csvData.filter(row => row.isRecebimento);
    const transacoesPagamento = csvData.filter(row => row.isPagamento);
    const transacoesTarifaReembolsoTaxa = csvData.filter(row => row.isTarifa || row.isReembolso || row.isTaxa || row.isJuros || row.isTarifaRJX);
    
    console.log('📄 GERANDO CSV PARA QUESTOR');
    console.log('📊 Total de recebimentos a processar:', transacoesRecebimento.length);
    console.log('📊 Total de pagamentos a processar:', transacoesPagamento.length);
    console.log('📊 Total de tarifas/reembolsos/taxas/juros a processar:', transacoesTarifaReembolsoTaxa.length);

    let virgulasRemovidas = 0;
    let nomesMapeados = 0;

    // Gerar as linhas do CSV para recebimentos
    transacoesRecebimento.forEach((row, index) => {
      const dataFormatada = formatarDataCSV(row.data);
      const contaDebito = 8;
      const contaCredito = 142;
      const valor = row.valor.toFixed(2); // Formato float com ponto decimal
      const zero = 0;
      
      // Processar descrição: remover acentos, converter para maiúsculo e REMOVER VÍRGULAS
      const descricaoOriginal = row.descricao.trim().replace(/^'/, ''); // Remove aspas simples no início
      const descricaoSemAcento = removerAcentosEMaiusculo(descricaoOriginal);
      const descricaoFinal = removerVirgulas(descricaoSemAcento); // CRÍTICO: remover vírgulas
      
      // Contar vírgulas removidas
      if (descricaoSemAcento !== descricaoFinal) {
        virgulasRemovidas++;
        if (virgulasRemovidas <= 5) {
          console.log(`📄 Vírgula removida da descrição: "${descricaoSemAcento}" -> "${descricaoFinal}"`);
        }
      }
      
      const descricao = `"${descricaoFinal}"`; // Descrição entre aspas

      // Formato: Data,ContaDebito,ContaCredito,Valor,Zero,Descrição
      const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
      linhasCSV.push(linha);
      
      if (index < 3) {
        console.log(`📄 RECEBIMENTO ${index + 1}: Débito=${contaDebito}, Crédito=${contaCredito}, Valor=${valor}`);
      }
    });

    // Gerar as linhas do CSV para pagamentos (exceto RJX TERCEIRIZACOES)
    transacoesPagamento.forEach((row, index) => {
      const dataFormatada = formatarDataCSV(row.data);
      
      // Determinar código de débito baseado em códigos especiais (ordem de prioridade)
      let contaDebito = 0; // Padrão
      
      // PRIORIDADE 1: Verificar se há nome no dicionário de mapeamento
      const codigoDetectado = detectarCodigoDebitoPorNome(row.descricao);
      if (codigoDetectado) {
        contaDebito = codigoDetectado;
        nomesMapeados++;
      }
      // PRIORIDADE 2: Códigos especiais baseados em padrões na descrição
      else if (row.codigoDebito4873) {
        contaDebito = 4873;
      } else if (row.codigoDebito4874) {
        contaDebito = 4874;
      } else if (row.codigoDebito4510) {
        contaDebito = 4510;
      } else if (row.codigoDebito4538) {
        contaDebito = 4538;
      } else if (row.codigoDebito4875) {
        contaDebito = 4875;
      } else if (row.codigoDebito4868) {
        contaDebito = 4868;
      } else if (row.codigoDebito4870) {
        contaDebito = 4870;
      } else if (row.codigoDebito4864) {
        contaDebito = 4864;
      } else if (row.codigoDebito4478) {
        contaDebito = 4478;
      } else if (row.codigoDebito4698) {
        contaDebito = 4698;
      } else if (row.codigoDebito617) {
        contaDebito = 617;
      } else if (row.codigoDebito4674) {
        contaDebito = 4674;
      }
      
      const contaCredito = 8;
      const valor = row.valor.toFixed(2); // Formato float com ponto decimal
      const zero = 0;
      
      // Processar descrição: remover acentos, converter para maiúsculo e REMOVER VÍRGULAS
      const descricaoOriginal = row.descricao.trim().replace(/^'/, ''); // Remove aspas simples no início
      const descricaoSemAcento = removerAcentosEMaiusculo(descricaoOriginal);
      const descricaoFinal = removerVirgulas(descricaoSemAcento); // CRÍTICO: remover vírgulas
      
      // Contar vírgulas removidas
      if (descricaoSemAcento !== descricaoFinal) {
        virgulasRemovidas++;
      }
      
      const descricao = `"${descricaoFinal}"`; // Descrição entre aspas

      // Formato: Data,ContaDebito,ContaCredito,Valor,Zero,Descrição
      const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
      linhasCSV.push(linha);
      
      if (index < 5 || codigoDetectado || contaDebito !== 0) {
        const origem = codigoDetectado ? ' [NOME MAPEADO]' : (contaDebito !== 0 ? ' [CÓDIGO ESPECIAL]' : '');
        console.log(`📄 PAGAMENTO ${index + 1}: Débito=${contaDebito}, Crédito=${contaCredito}, Valor=${valor}${origem}`);
      }
    });

    // Gerar as linhas do CSV para Tarifa, Reembolso e Taxa (Débito: 4698, Crédito: 8)
    transacoesTarifaReembolsoTaxa.forEach((row, index) => {
      const dataFormatada = formatarDataCSV(row.data);
      const contaDebito = 4698;
      const contaCredito = 8;
      const valor = row.valor.toFixed(2); // Formato float com ponto decimal
      const zero = 0;
      
      // Processar descrição: remover acentos, converter para maiúsculo e REMOVER VÍRGULAS
      const descricaoOriginal = row.descricao.trim().replace(/^'/, ''); // Remove aspas simples no início
      const descricaoSemAcento = removerAcentosEMaiusculo(descricaoOriginal);
      const descricaoFinal = removerVirgulas(descricaoSemAcento); // CRÍTICO: remover vírgulas
      
      if (descricaoSemAcento !== descricaoFinal) {
        virgulasRemovidas++;
      }
      
      const descricao = `"${descricaoFinal}"`; // Descrição entre aspas

      // Formato: Data,ContaDebito,ContaCredito,Valor,Zero,Descrição
      const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
      linhasCSV.push(linha);
      
      if (index < 3) {
        console.log(`📄 TARIFA/TAXA/REEMBOLSO/JUROS ${index + 1}: Débito=${contaDebito}, Crédito=${contaCredito}, Valor=${valor}`);
      }
    });

    console.log('📄 CSV gerado com', linhasCSV.length, 'linhas');
    console.log('📄 - Recebimentos:', transacoesRecebimento.length);
    console.log('📄 - Pagamentos:', transacoesPagamento.length);
    console.log('📄 - Tarifas/Reembolsos/Taxas/Juros:', transacoesTarifaReembolsoTaxa.length);
    console.log('📄 - Vírgulas removidas das descrições:', virgulasRemovidas);
    if (nomesMapeados > 0) {
      console.log(`📝 - Pagamentos com nomes mapeados (dicionário): ${nomesMapeados}`);
    }

    // Criar e baixar o arquivo CSV
    const csvContent = linhasCSV.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TOV SOLUCOES LANCAMENTOS CSV QUESTOR ${extrairCompetencia()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Função para baixar JSON dos dados
  const downloadJSON = () => {
    const dataToDownload = {
      summary,
      transactions: csvData,
      processedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(dataToDownload, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tov-solucoes-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Função para copiar JSON
  const copyJSON = () => {
    const dataToDownload = {
      summary,
      transactions: csvData,
      processedAt: new Date().toISOString()
    };
    
    navigator.clipboard.writeText(JSON.stringify(dataToDownload, null, 2))
      .then(() => alert('JSON copiado para a área de transferência!'))
      .catch(() => alert('Erro ao copiar JSON'));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Cabeçalho */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to={createPageUrl("Operations")}>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Voltar para Operações
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">TOV SOLUCOES</h1>
              <p className="text-gray-600 mt-1">Gerenciamento de operações contábeis TOV Soluções</p>
            </div>
          </div>
        </div>

        {/* Área de Upload */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload do Arquivo CSV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                isDragOver 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-300 hover:border-green-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-green-100 rounded-full">
                  <FileText className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    {isProcessing ? 'Processando arquivo...' : 'Arraste e solte o arquivo CSV aqui'}
                  </p>
                  <p className="text-gray-500 mt-1">
                    ou clique para selecionar o arquivo
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  disabled={isProcessing}
                >
                  <Upload className="w-4 h-4" />
                  Selecionar Arquivo
                </Button>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
          </CardContent>
        </Card>

        {/* Resumo Financeiro */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
              <Card className="bg-indigo-50 border-indigo-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-indigo-600 text-sm font-medium">Saldo Inicial</p>
                    <p className="text-2xl font-bold text-indigo-700">
                      {formatCurrency(summary.saldoInicial)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">Valor Recebimentos</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {formatCurrency(summary.valorTotalRecebimentos)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-red-600 text-sm font-medium">Valor Pagamentos</p>
                    <p className="text-2xl font-bold text-red-700">
                      {formatCurrency(summary.valorTotalPagamentos)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-purple-600 text-sm font-medium">Saldo Líquido</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {formatCurrency(summary.saldoInicial + summary.valorTotalRecebimentos - summary.valorTotalPagamentos - summary.valorTotalTarifasTaxas)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-green-600 text-sm font-medium">Saldo Final da Conta</p>
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(summary.saldoFinal)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Botões de Ação */}
            <div className="flex flex-wrap gap-4 mb-8">
              <Button onClick={downloadJSON} className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Baixar JSON
              </Button>
              <Button variant="outline" onClick={copyJSON} className="flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Copiar JSON
              </Button>
              <Button 
                onClick={gerarCSVFormatado} 
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                <Download className="w-4 h-4" />
                Baixar CSV Formatado para Questor
              </Button>
            </div>

            {/* Tabela de Transações Processadas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Transações Processadas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-700">Tipo</th>
                        <th className="text-left p-3 font-medium text-gray-700">Data</th>
                        <th className="text-left p-3 font-medium text-gray-700">Descrição</th>
                        <th className="text-right p-3 font-medium text-gray-700">Valor</th>
                        <th className="text-right p-3 font-medium text-gray-700">Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.filter(row => row.isRecebimento || row.isPagamento || row.isTarifa || row.isReembolso || row.isTaxa || row.isJuros).map((row, index) => {
                        // Limpar descrição para exibição (remover acentos e caracteres especiais)
                        const descricaoLimpa = removerAcentosEMaiusculo(row.descricao.trim().replace(/^'/, ''));
                        
                        return (
                          <tr key={index} className={`border-t ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                row.isRecebimento ? 'bg-green-100 text-green-700' : 
                                row.isPagamento ? 'bg-orange-100 text-orange-700' : 
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {row.isRecebimento ? 'Recebimento' : row.isPagamento ? 'Pagamento' : 'Tarifa/Taxa'}
                              </span>
                            </td>
                            <td className="p-3 text-gray-700">{row.data}</td>
                            <td className="p-3 text-gray-700 text-xs">{descricaoLimpa}</td>
                            <td className={`p-3 text-right font-medium ${
                              row.isRecebimento ? 'text-green-600' : 
                              row.isPagamento ? 'text-orange-600' : 
                              'text-yellow-600'
                            }`}>
                              {formatCurrency(row.valor)}
                            </td>
                            <td className="p-3 text-right text-blue-600 font-medium">
                              {formatCurrency(row.saldo)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
